import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams } from '../../types/kpi.types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Loader } from '../Loader';

interface RegistrationPatternAnalysisProps {
  queryParams: KpiQueryParams;
}

interface PatternData {
  dayOfMonth: number;
  totalRegistrations: number;
  medianDaysToFirstTransfer: number | null;
  usersWhoNeverTransferred: number;
  usersWhoNeverTransferredPercentage: number;
  usersWhoEventuallyTransferred: number;
}

const RegistrationPatternAnalysis: React.FC<RegistrationPatternAnalysisProps> = ({
  queryParams,
}) => {
  const [data, setData] = useState<PatternData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await analyticsService.getRegistrationDayOfMonthAnalysis({
          database: queryParams.database,
          companyId: queryParams.companyId,
          startDate: queryParams.startDate,
          endDate: queryParams.endDate,
        });
        // Filter out days with 0 registrations (days that don't exist in all months)
        const filtered = result.filter(row => row.totalRegistrations > 0);
        setData(filtered);
      } catch (err: any) {
        setError(err.message || 'Failed to load registration pattern data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.database, queryParams.companyId, queryParams.startDate, queryParams.endDate]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f' }}>
        Error: {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ padding: '20px', color: '#666' }}>
        No data available for analysis.
      </div>
    );
  }

  // Prepare data for charts
  const chartData = data.map(row => ({
    day: row.dayOfMonth,
    registrations: row.totalRegistrations,
    median: row.medianDaysToFirstTransfer,
    neverTransferred: row.usersWhoNeverTransferred,
    neverTransferredPercent: row.usersWhoNeverTransferredPercentage,
  }));

  // Sort by day of month
  const sortedData = [...data].sort((a, b) => a.dayOfMonth - b.dayOfMonth);

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600, color: '#333' }}>
          Registration Pattern Analysis
        </h2>
        <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '14px' }}>
          Analysis of user registrations by day of month, showing registration volume and median days to first transfer.
          {queryParams.companyId ? ' Showing data for selected company.' : ' Showing aggregated data across all companies.'}
        </p>

        {/* Registration Count Graph */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Registration Volume by Day of Month
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="day" 
                label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }}
                stroke="#666"
              />
              <YAxis 
                label={{ value: 'Number of Registrations', angle: -90, position: 'insideLeft' }}
                stroke="#666"
              />
              <Tooltip 
                formatter={(value: any) => [value.toLocaleString(), 'Registrations']}
                labelFormatter={(label) => `Day ${label} of Month`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <Legend />
              <Bar dataKey="registrations" fill="#4A90D9" name="Registrations" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Median Days to First Transfer Graph */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Median Days to First Transfer by Day of Month
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData.filter(d => d.median !== null)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="day" 
                label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }}
                stroke="#666"
              />
              <YAxis 
                label={{ value: 'Median Days to First Transfer', angle: -90, position: 'insideLeft' }}
                stroke="#666"
              />
              <Tooltip 
                formatter={(value: any) => [`${Number(value).toFixed(1)} days`, 'Median']}
                labelFormatter={(label) => `Day ${label} of Month`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="median" 
                stroke="#d32f2f" 
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Median Days to First Transfer"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* % Never Transferred Trendline */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            % Never Transferred by Day of Month
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="day" 
                label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }}
                stroke="#666"
              />
              <YAxis 
                label={{ value: '% Never Transferred', angle: -90, position: 'insideLeft' }}
                domain={[0, 100]}
                stroke="#666"
              />
              <Tooltip 
                formatter={(value: any) => [`${Number(value).toFixed(1)}%`, '% Never Transferred']}
                labelFormatter={(label) => `Day ${label} of Month`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="neverTransferredPercent" 
                stroke="#ff6b6b" 
                strokeWidth={2}
                dot={{ r: 4 }}
                name="% Never Transferred"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Detailed Table */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Detailed Table
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                    Day of<br/>Month
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                    Total<br/>Registrations
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#fff3e0' }}>
                    Median Days to<br/>First Transfer
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                    Never<br/>Transferred
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                    % Never<br/>Transferred
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', backgroundColor: '#e8f5e9' }}>
                    Eventually<br/>Transferred
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, index) => (
                  <tr
                    key={row.dayOfMonth}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                      {row.dayOfMonth}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd', fontWeight: 600 }}>
                      {row.totalRegistrations.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#fff3e0', fontWeight: 600 }}>
                      {row.medianDaysToFirstTransfer !== null ? `${row.medianDaysToFirstTransfer.toFixed(1)} days` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                      {row.usersWhoNeverTransferred.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee', fontWeight: 600 }}>
                      {row.usersWhoNeverTransferredPercentage.toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#555', backgroundColor: '#e8f5e9' }}>
                      {row.usersWhoEventuallyTransferred.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>
            📊 How to Read This Analysis
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>Day of Month:</strong> The calendar day (1-31) when users registered (e.g., day 5 = all users who registered on the 5th of any month)</li>
            <li><strong>Total Registrations:</strong> Total number of users who registered on this day of the month (within the selected date range)</li>
            <li><strong>Median Days to First Transfer:</strong> Median number of days from registration to first transfer (only for users who eventually transferred)</li>
            <li><strong>Never Transferred:</strong> Count and percentage of users who registered on this day but never made a transfer</li>
            <li><strong>Eventually Transferred:</strong> Count of users who registered on this day and eventually made at least one transfer</li>
          </ul>
          <p style={{ margin: '12px 0 0 0', color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
            💡 <strong>Key Insight:</strong> This analysis helps identify if certain days of the month correlate with different user behavior patterns. 
            For example, do users who register at the beginning of the month (payday?) transfer faster than those who register mid-month?
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPatternAnalysis;

